import { useDataProvider, useGetOne, useTranslate } from 'ra-core';
import { useParams } from 'react-router-dom';
import { CircularPerformanceIndicator } from './CircularPerformanceIndicator';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { UnitPerformanceInsightsCard } from './UnitPerformanceInsightsCard';
import { UnitPerformanceSuggestedTrainingCard } from './UnitPerformanceSuggestedTrainingCard';
import { type AdminDataProvider } from '../../../api/adminDataProvider';
import {
  type UnitPerformanceDataResponseEntry,
  type UnitPerformanceInsightsResponse,
  type UnitPerformanceScoreResponse,
} from '../../../api/customEndpoints/fetchUnitPerformanceScores';
import { UnitPerformanceKpiData } from './UnitPerformanceKpiData';
import { useMemo } from 'react';
import { MoonstarTitle } from '../../Moonstar/MoonstarTitle';
import { Card } from '../../../components/ui/card';
import { Error } from '../../../components/admin/error';
import { Loading } from '../../../components/admin/loading';

export function UnitPerformancePage() {
  const { unitId } = useParams<{ unitId: string }>();
  const numericUnitId = Number(unitId);
  const unit = useGetOne<{ id: number; name: string }>('units', {
    id: numericUnitId,
  });
  const dataProvider = useDataProvider<AdminDataProvider>();
  const translate = useTranslate();

  const unitKpiData = useQuery({
    queryKey: ['unitKpiData', numericUnitId],
    queryFn: async () => dataProvider.fetchUnitPerformanceData(numericUnitId),
  });

  const unitInsights = useQuery({
    queryKey: ['unitInsights', numericUnitId],
    queryFn: async () =>
      dataProvider.fetchUnitPerformanceInsights(numericUnitId),
  });

  const unitScores = useQuery({
    queryKey: ['unitScores', numericUnitId],
    queryFn: async () => dataProvider.fetchUnitPerformanceScore(numericUnitId),
  });

  return (
    <>
      <MoonstarTitle
        breadcrumb={[
          translate('resources.units.name', { count: 2 }),
          unit.data?.name || translate('resources.units.name', { count: 1 }),
          translate('resources.units.fields.performance'),
        ]}
      />
      {unit.isPending || unitScores.isPending ? (
        <Loading />
      ) : unit.isError || unitScores.isError ? (
        <Error
          resetErrorBoundary={() => {
            void unit.refetch();
            void unitScores.refetch();
          }}
          error={unit.error ?? unitScores.error}
        />
      ) : (
        <UnitPerformanceDisplay
          unit={unit.data}
          unitPerformanceScores={unitScores.data}
          unitInsights={unitInsights}
          unitKpiData={unitKpiData}
        />
      )}
    </>
  );
}

function UnitPerformanceDisplay({
  unit,
  unitPerformanceScores,
  unitInsights,
  unitKpiData,
}: {
  unit: { id: number; name: string };
  unitPerformanceScores: UnitPerformanceScoreResponse;
  unitInsights: UseQueryResult<UnitPerformanceInsightsResponse>;
  unitKpiData: UseQueryResult<UnitPerformanceDataResponseEntry[]>;
}) {
  const translate = useTranslate();

  return (
    <div className="space-y-2 p-1">
      <h1 className="text-xl font-semibold">
        {translate('moonstar.unitPerformance.title', { name: unit.name })}
      </h1>
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <CircularPerformanceIndicator
              color="oklch(75% 0.15 58)"
              label={translate('moonstar.unitPerformance.unitScore')}
              value={parseFloat(unitPerformanceScores.myUnitScore)}
            />
            <CircularPerformanceIndicator
              color="oklch(75% 0.06 220)"
              label={translate('moonstar.unitPerformance.companyScore')}
              value={parseFloat(unitPerformanceScores.companyAvgScore)}
            />
            <div className="self-center">
              <p className="text-sm font-bold">
                {translate('moonstar.unitPerformance.vsCompany', {
                  value: new Intl.NumberFormat(undefined, {
                    maximumFractionDigits: 1,
                    signDisplay: 'always',
                  }).format(
                    parseFloat(unitPerformanceScores.myUnitScore) -
                      parseFloat(unitPerformanceScores.companyAvgScore)
                  ),
                })}
              </p>
              <InTheXPercentileText
                percentile={unitPerformanceScores.myUnitPercentile}
              />
            </div>
          </div>
        </Card>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <UnitPerformanceInsightsCard
              unitInsights={unitInsights}
              unitId={unit.id.toString()}
            />
          </Card>
          <Card>
            <UnitPerformanceSuggestedTrainingCard />
          </Card>
        </div>
        <UnitPerformanceKpiData kpiData={unitKpiData} unitName={unit.name} />
      </div>
    </div>
  );
}

function InTheXPercentileText({ percentile }: { percentile: string }) {
  const translate = useTranslate();

  const scoreDisplay = useMemo(() => {
    const percNumber = parseFloat(percentile);
    const percFormatter = new Intl.NumberFormat(undefined, {
      style: 'percent',
      maximumFractionDigits: 0,
    });
    const top = (value: number) =>
      translate('moonstar.unitPerformance.percentile.top', {
        percent: percFormatter.format(value),
      });
    const bottom = (value: number) =>
      translate('moonstar.unitPerformance.percentile.bottom', {
        percent: percFormatter.format(value),
      });

    if (percNumber <= 0.01) {
      return top(0.01);
    } else if (percNumber <= 0.05) {
      return top(0.05);
    } else if (percNumber <= 0.2) {
      return top(0.2);
    } else if (percNumber <= 0.4) {
      return top(0.4);
    } else if (percNumber <= 0.6) {
      return top(0.6);
    } else if (percNumber <= 0.8) {
      return bottom(0.4);
    } else {
      return bottom(0.2);
    }
  }, [percentile, translate]);

  return <span className="text-xs">{scoreDisplay}</span>;
}
